// src/components/operationCenter/widgets/SpreadsheetWidget.tsx
// Spreadsheet widget for dashboard - can display standalone data or link to a Sheet document

import React, { useCallback, useMemo } from "react";
import { Table2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { WidgetProps } from "@/types/dashboard";
import { useDashboardStore } from "@/stores/dashboardStore";

// Import shared components and hooks from sheets feature
import {
  Spreadsheet,
  SheetSelector,
  useSheetsStore,
  useSheetById,
  parseCellKey,
  type Sheet,
  type CellFormat,
} from "@/features/sheets";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SpreadsheetConfig {
  // Link to a sheet in the sheets store (takes precedence over local data)
  linkedSheetId?: string | undefined;
  // Local data (used when not linked to a sheet)
  data?: string[][] | undefined;
  formats?: Record<string, CellFormat> | undefined;
  rows?: number | undefined;
  cols?: number | undefined;
  colWidths?: number[] | undefined;
  // Index signature to satisfy WidgetConfig constraint
  [key: string]: unknown;
}

const DEFAULT_ROWS = 10;
const DEFAULT_COLS = 6;
const DEFAULT_COL_WIDTH = 100;

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const SpreadsheetWidget: React.FC<WidgetProps<SpreadsheetConfig>> = ({
  widget,
}) => {
  const config = widget.config as SpreadsheetConfig;
  const updateWidgetConfig = useDashboardStore((s) => s.updateWidgetConfig);
  const navigate = useNavigate();

  // Sheets store
  const sheetsStore = useSheetsStore();
  const linkedSheet = useSheetById(config.linkedSheetId ?? null);

  // Determine if we're using a linked sheet or local data
  const isLinked = !!config.linkedSheetId && !!linkedSheet;

  // Convert widget config to Sheet format for local mode
  const localSheet: Sheet = useMemo(() => {
    const rows = config.rows || DEFAULT_ROWS;
    const cols = config.cols || DEFAULT_COLS;

    let data: string[][];
    if (config.data && Array.isArray(config.data) && config.data.length > 0) {
      data = [];
      for (let r = 0; r < rows; r++) {
        const sourceRow = config.data[r];
        const newRow: string[] = [];
        for (let c = 0; c < cols; c++) {
          newRow.push(
            sourceRow && Array.isArray(sourceRow) && sourceRow[c] != null
              ? String(sourceRow[c])
              : ""
          );
        }
        data.push(newRow);
      }
    } else {
      data = Array.from({ length: rows }, () =>
        Array.from({ length: cols }, () => "")
      );
    }

    return {
      id: widget.id,
      name: widget.title || "Spreadsheet",
      data,
      formats: config.formats || {},
      colWidths: config.colWidths || Array(cols).fill(DEFAULT_COL_WIDTH),
      rows,
      cols,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }, [widget.id, widget.title, config]);

  // The sheet to display
  const displaySheet = isLinked ? linkedSheet! : localSheet;

  // ─────────────────────────────────────────────────────────────────────────────
  // Change handlers - route to correct store
  // ─────────────────────────────────────────────────────────────────────────────

  const handleDataChange = useCallback(
    (data: string[][]) => {
      if (isLinked && config.linkedSheetId) {
        // Batch update to sheets store
        const updates: Array<{ row: number; col: number; value: string }> = [];
        data.forEach((row, rowIdx) => {
          row.forEach((value, colIdx) => {
            const currentValue = linkedSheet?.data[rowIdx]?.[colIdx];
            if (value !== currentValue) {
              updates.push({ row: rowIdx, col: colIdx, value });
            }
          });
        });
        if (updates.length > 0) {
          sheetsStore.updateCells(config.linkedSheetId, updates);
        }
      } else {
        // Save to widget config
        updateWidgetConfig(widget.id, {
          ...config,
          data,
          rows: data.length,
          cols: data[0]?.length || DEFAULT_COLS,
        });
      }
    },
    [isLinked, config, linkedSheet?.data, sheetsStore, updateWidgetConfig, widget.id]
  );

  const handleFormatsChange = useCallback(
    (formats: Record<string, CellFormat>) => {
      if (isLinked && config.linkedSheetId) {
        Object.entries(formats).forEach(([key, format]) => {
          const pos = parseCellKey(key);
          if (pos) {
            sheetsStore.setCellFormat(config.linkedSheetId!, pos.row, pos.col, format);
          }
        });
      } else {
        updateWidgetConfig(widget.id, { ...config, formats });
      }
    },
    [isLinked, config, sheetsStore, updateWidgetConfig, widget.id]
  );

  const handleColWidthsChange = useCallback(
    (colWidths: number[]) => {
      if (isLinked && config.linkedSheetId) {
        colWidths.forEach((width, col) => {
          if (width !== linkedSheet?.colWidths[col]) {
            sheetsStore.setColumnWidth(config.linkedSheetId!, col, width);
          }
        });
      } else {
        updateWidgetConfig(widget.id, { ...config, colWidths });
      }
    },
    [isLinked, config, linkedSheet?.colWidths, sheetsStore, updateWidgetConfig, widget.id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Structure operations - route to correct store
  // ─────────────────────────────────────────────────────────────────────────────

  const handleAddRow = useCallback(
    (afterRow?: number) => {
      if (isLinked && config.linkedSheetId) {
        sheetsStore.addRow(config.linkedSheetId, afterRow);
      } else {
        const insertAt = afterRow !== undefined ? afterRow + 1 : localSheet.rows;
        const newData = [
          ...localSheet.data.slice(0, insertAt),
          Array(localSheet.cols).fill(""),
          ...localSheet.data.slice(insertAt),
        ];
        updateWidgetConfig(widget.id, {
          ...config,
          data: newData,
          rows: localSheet.rows + 1,
        });
      }
    },
    [isLinked, config, localSheet, sheetsStore, updateWidgetConfig, widget.id]
  );

  const handleDeleteRow = useCallback(
    (row: number) => {
      if (isLinked && config.linkedSheetId) {
        sheetsStore.deleteRow(config.linkedSheetId, row);
      } else {
        const newData = localSheet.data.filter((_, i) => i !== row);
        updateWidgetConfig(widget.id, {
          ...config,
          data: newData,
          rows: localSheet.rows - 1,
        });
      }
    },
    [isLinked, config, localSheet, sheetsStore, updateWidgetConfig, widget.id]
  );

  const handleAddColumn = useCallback(
    (afterCol?: number) => {
      if (isLinked && config.linkedSheetId) {
        sheetsStore.addColumn(config.linkedSheetId, afterCol);
      } else {
        const insertAt = afterCol !== undefined ? afterCol + 1 : localSheet.cols;
        const newData = localSheet.data.map((row) => [
          ...row.slice(0, insertAt),
          "",
          ...row.slice(insertAt),
        ]);
        const newColWidths = [
          ...localSheet.colWidths.slice(0, insertAt),
          DEFAULT_COL_WIDTH,
          ...localSheet.colWidths.slice(insertAt),
        ];
        updateWidgetConfig(widget.id, {
          ...config,
          data: newData,
          colWidths: newColWidths,
          cols: localSheet.cols + 1,
        });
      }
    },
    [isLinked, config, localSheet, sheetsStore, updateWidgetConfig, widget.id]
  );

  const handleDeleteColumn = useCallback(
    (col: number) => {
      if (isLinked && config.linkedSheetId) {
        sheetsStore.deleteColumn(config.linkedSheetId, col);
      } else {
        const newData = localSheet.data.map((row) =>
          row.filter((_, i) => i !== col)
        );
        const newColWidths = localSheet.colWidths.filter((_, i) => i !== col);
        updateWidgetConfig(widget.id, {
          ...config,
          data: newData,
          colWidths: newColWidths,
          cols: localSheet.cols - 1,
        });
      }
    },
    [isLinked, config, localSheet, sheetsStore, updateWidgetConfig, widget.id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Sheet selector handlers
  // ─────────────────────────────────────────────────────────────────────────────

  const handleSelectSheet = useCallback(
    (sheetId: string | null) => {
      updateWidgetConfig(widget.id, {
        ...config,
        linkedSheetId: sheetId || undefined,
      });
    },
    [updateWidgetConfig, widget.id, config]
  );

  const handleCreateNewSheet = useCallback(() => {
    // Create new sheet with current local data preserved
    const newSheet = sheetsStore.importSheet(
      widget.title || `Widget Sheet ${Date.now()}`,
      localSheet.data,
      config.formats,
      config.colWidths
    );
    updateWidgetConfig(widget.id, {
      ...config,
      linkedSheetId: newSheet.id,
    });
  }, [sheetsStore, updateWidgetConfig, widget.id, config, localSheet.data, widget.title]);

  const handleOpenInFullPage = useCallback(() => {
    if (config.linkedSheetId) {
      navigate(`/app/workspace/sheets/${config.linkedSheetId}`);
    }
  }, [navigate, config.linkedSheetId]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Widget Header with Sheet Selector */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
        <Table2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
        <span className="text-xs uppercase tracking-wide text-slate-300">
          Spreadsheet
        </span>
        <SheetSelector
          linkedSheetId={config.linkedSheetId}
          onSelectSheet={handleSelectSheet}
          onCreateNewSheet={handleCreateNewSheet}
          onOpenInFullPage={handleOpenInFullPage}
        />
      </div>

      {/* Spreadsheet Component with toolbar showing sheet name */}
      <Spreadsheet
        sheet={displaySheet}
        onDataChange={handleDataChange}
        onFormatsChange={handleFormatsChange}
        onColWidthsChange={handleColWidthsChange}
        onAddRow={handleAddRow}
        onDeleteRow={handleDeleteRow}
        onAddColumn={handleAddColumn}
        onDeleteColumn={handleDeleteColumn}
        showToolbar={true}
        className="flex-1"
      />
    </div>
  );
};

export default SpreadsheetWidget;
